import useGuestReviewTemplateMutations from 'components/domain/review/useGuestReviewTemplateMutations';
import {
  AdditionalManualReviewPayload,
  GuestReviewTemplate,
  GuestReviewTemplatePayload,
  GuestReviewTemplateRatingCategory,
  ManualReviewAutomatedReviewLogPayload,
} from 'models/automation/Reviews';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import { useTranslation } from 'react-i18next';
import useManualReviewMutation from 'components/domain/review/automatedReviewLogs/useManualReviewMutation';
import {
  ReviewTemplateSchemaFormValues,
  ReviewTemplateSchemaVrboFormValues,
} from './AddEditReviewTemplateModal.schema';

const useReviewTemplateFormSubmit = () => {
  const { agencyUid } = useAppUser();
  const { notifyError } = useNotify();
  const { t } = useTranslation();
  const { mutateAsync: submitManualReview } = useManualReviewMutation();
  const { addReviewTemplate, updateReviewTemplate } =
    useGuestReviewTemplateMutations();

  const extractLineBreaks = (content: string) => {
    return content.replace(/\n/g, ' ');
  };

  const handleSubmit = async ({
    values,
    reviewTemplate,
    manualReviewPayload,
  }: {
    values: ReviewTemplateSchemaFormValues | ReviewTemplateSchemaVrboFormValues;
    reviewTemplate?: GuestReviewTemplate;
    manualReviewPayload?: AdditionalManualReviewPayload;
  }) => {
    // @ts-expect-error privateFeedback is optional for VRBO manual review
    const payload: GuestReviewTemplatePayload = {
      agencyUid: agencyUid!,
      title: 'title' in values ? values.title : '',
      content:
        'content_htmlContent' in values
          ? extractLineBreaks(values.content_htmlContent)
          : '',
      rate: values.rate,
      isGuestRecommended: values.isGuestRecommended === 'yes',
      ...('privateFeedback' in values
        ? { privateFeedback: values.privateFeedback ?? null }
        : { author: null }),
      ratingCategories: [
        {
          category: GuestReviewTemplateRatingCategory.CLEANLINESS,
          rate: values.cleanliness,
        },
        {
          category: GuestReviewTemplateRatingCategory.COMMUNICATION,
          rate: values.communication,
        },
        {
          category: GuestReviewTemplateRatingCategory.RESPECT_HOUSE_RULES,
          rate: values.respectHouseRules,
        },
      ],
    };

    if (manualReviewPayload !== undefined) {
      // @ts-expect-error since agencyUid is declared above, the spread method to delete does not work
      delete payload.agencyUid;

      const manualReviewPayloadToSend: ManualReviewAutomatedReviewLogPayload = {
        ...payload,
        ...manualReviewPayload,
      };

      await submitManualReview(manualReviewPayloadToSend, {
        onError: () => {
          notifyError(
            t(
              'pageAutomations.reviews.automatedReviewLogs.actions.manualReview.error',
            ),
          );
        },
      });
      return;
    }

    if (reviewTemplate && 'uid' in reviewTemplate) {
      await updateReviewTemplate({
        payload,
        uid: reviewTemplate.uid,
      });
    } else {
      await addReviewTemplate({
        payload,
      });
    }
  };

  return {
    handleSubmit,
  };
};

export default useReviewTemplateFormSubmit;
